import type { Feature, FeatureCollection, Geometry } from 'geojson';

export type BoundaryImpact = { outsideAreas: any[]; unlocatedAreas: any[]; outsidePatternPoints: any[] };
export const impactFeatureId = (kind: 'area' | 'point', id: number | string) => `${kind}:${id}`;

const geographicGeometry = (input: any): Geometry | null => {
  let geometry = input;
  if (typeof geometry === 'string') { try { geometry = JSON.parse(geometry); } catch { return null; } }
  if (!geometry || !['Point', 'Polygon', 'MultiPolygon'].includes(geometry.type)) return null;
  const position = (p: any): boolean => Array.isArray(p) && p.length >= 2
    && typeof p[0] === 'number' && typeof p[1] === 'number'
    && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) <= 180 && Math.abs(p[1]) <= 90;
  const ring = (r: any): boolean => Array.isArray(r) && r.length >= 4 && r.every(position)
    && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1];
  const polygon = (p: any): boolean => Array.isArray(p) && p.length > 0 && p.every(ring);
  if (geometry.type === 'Point' ? !position(geometry.coordinates)
    : geometry.type === 'Polygon' ? !polygon(geometry.coordinates)
      : !Array.isArray(geometry.coordinates) || !geometry.coordinates.length || !geometry.coordinates.every(polygon)) return null;
  return geometry as Geometry;
};

export const boundaryImpactFeatures = (impact?: BoundaryImpact): FeatureCollection => {
  const features: Feature[] = [];
  const area = (row: any, status: string) => {
    const geometry = geographicGeometry(row.geom);
    if (geometry) features.push({ type: 'Feature', geometry, properties: {
      featureId: impactFeatureId('area', row.id), label: row.name || `Area #${row.id}`, status,
      archived: Boolean(row.archivedAt), locationSource: row.locationSource || null,
    } });
  };
  impact?.outsideAreas.forEach(row => area(row, 'Outside boundary'));
  impact?.unlocatedAreas.forEach(row => area(row, 'Unlocated area'));
  impact?.outsidePatternPoints.forEach(row => {
    const geometry = geographicGeometry({ type: 'Point', coordinates: [row.gpsLon, row.gpsLat] });
    if (geometry) features.push({ type: 'Feature', geometry, properties: {
      featureId: impactFeatureId('point', row.id), label: row.label || `Point #${row.id}`,
      status: 'Outside pattern point', patternName: row.patternName || `Pattern #${row.patternId}`,
    } });
  });
  return { type: 'FeatureCollection', features };
};

export const geometryPositions = (geometry?: any): [number, number][] => {
  if (!geometry) return [];
  const positions: [number, number][] = [];
  const visit = (coordinates: any) => {
    if (!Array.isArray(coordinates)) return;
    if (typeof coordinates[0] === 'number') {
      if (Number.isFinite(coordinates[0]) && Number.isFinite(coordinates[1])) positions.push([coordinates[0], coordinates[1]]);
    } else coordinates.forEach(visit);
  };
  visit(geometry.coordinates);
  return positions;
};
